// eslint-disable-next-line import/extensions
import { FieldsState } from 'pages/rules/type';

export const getReadableFormula = (type: string): string => {
  const formulas: Record<string, string> = {
    percentage: '(Field1 / 100) * Field2',
    percentage_min_fixed_max_variable:
      'MAX(Min_value, MIN(Max_Field, ((Field1 / 100) * Field2)))',
    percentage_min_fixed_max_fixed:
      'MAX(Min_value, MIN(Max_value, ((Field1 / 100) * Field2)))',
    percentage_min_variable_max_variable:
      'MAX(Min_Field, MIN(Max_Field, ((Field1 / 100) * Field2)))',
    percentage_min_variable_max_fixed:
      'MAX(Min_Field, MIN(Max_value, ((Field1 / 100) * Field2)))',
    percentage_min_fixed: 'MAX(Min_value, ((Field1 / 100) * Field2))',
    percentage_min_variable: 'MAX(Min_Field, ((Field1 / 100) * Field2))',
    percentage_max_fixed: 'MIN(Max_value, ((Field1 / 100) * Field2))',
    percentage_max_variable: 'MIN(Max_Field, ((Field1 / 100) * Field2))',
    flat: 'Field1',
    flat_min_variable_max_fixed: 'MAX(Min_Field, MIN(Max_value, Field1))',
    flat_min_variable_max_variable: 'MAX(Min_Field, MIN(Max_Field, Field1))',
    flat_min_fixed_max_fixed: 'MAX(Min_value, MIN(Max_value, Field1))',
    flat_min_fixed_max_variable: 'MAX(Min_value, MIN(Max_Field, Field1))',
    flat_max_fixed: 'MIN(Max_value, Field1)',
    flat_max_variable: 'MIN(Max_Field, Field1)',
    flat_min_fixed: 'MAX(Min_value, Field1)',
    flat_min_variable: 'MAX(Min_Field, Field1)',
  };
  return formulas[type] || '';
};

export const buildJsonLogic = (type: string, fields: FieldsState): any => {
  const { field1, field2, minValue, maxValue, minField, maxField } = fields;

  // Helper to keep the switch cases clean
  const perc = { '*': [{ '/': [{ var: field1 }, 100] }, { var: field2 }] };
  const flat = { var: field1 };

  switch (type) {
    case 'percentage':
      return field1 && field2 ? perc : null;

    case 'percentage_min_fixed_max_variable':
      return field1 && field2 && minValue && maxField
        ? { max: [Number(minValue), { min: [{ var: maxField }, perc] }] }
        : null;

    case 'percentage_min_fixed_max_fixed':
      return field1 && field2 && minValue && maxValue
        ? { max: [Number(minValue), { min: [Number(maxValue), perc] }] }
        : null;

    case 'percentage_min_variable_max_variable':
      return field1 && field2 && minField && maxField
        ? { max: [{ var: minField }, { min: [{ var: maxField }, perc] }] }
        : null;

    case 'percentage_min_variable_max_fixed':
      return field1 && field2 && minField && maxValue
        ? { max: [{ var: minField }, { min: [Number(maxValue), perc] }] }
        : null;

    case 'percentage_min_fixed':
      return field1 && field2 && minValue
        ? { max: [Number(minValue), perc] }
        : null;

    case 'percentage_min_variable':
      return field1 && field2 && minField
        ? { max: [{ var: minField }, perc] }
        : null;

    case 'percentage_max_fixed':
      return field1 && field2 && maxValue
        ? { min: [Number(maxValue), perc] }
        : null;

    case 'percentage_max_variable':
      return field1 && field2 && maxField
        ? { min: [{ var: maxField }, perc] }
        : null;

    case 'flat':
      return field1 ? flat : null;

    case 'flat_min_variable_max_fixed':
      return field1 && minField && maxValue
        ? { max: [{ var: minField }, { min: [Number(maxValue), flat] }] }
        : null;

    case 'flat_min_variable_max_variable':
      return field1 && minField && maxField
        ? { max: [{ var: minField }, { min: [{ var: maxField }, flat] }] }
        : null;

    case 'flat_min_fixed_max_fixed':
      return field1 && minValue && maxValue
        ? { max: [Number(minValue), { min: [Number(maxValue), flat] }] }
        : null;

    case 'flat_min_fixed_max_variable':
      return field1 && minValue && maxField
        ? { max: [Number(minValue), { min: [{ var: maxField }, flat] }] }
        : null;

    case 'flat_max_fixed':
      return field1 && maxValue ? { min: [Number(maxValue), flat] } : null;

    case 'flat_max_variable':
      return field1 && maxField ? { min: [{ var: maxField }, flat] } : null;

    case 'flat_min_fixed':
      return field1 && minValue ? { max: [Number(minValue), flat] } : null;

    case 'flat_min_variable':
      return field1 && minField ? { max: [{ var: minField }, flat] } : null;

    default:
      return null;
  }
};

export const getQueryWithValues = (
  type: string,
  fields: FieldsState
): string => {
  let query = getReadableFormula(type);

  if (fields.field1) query = query.replace(/Field1/g, fields.field1);
  if (fields.field2) query = query.replace(/Field2/g, fields.field2);
  if (fields.minValue) query = query.replace(/Min_value/g, fields.minValue);
  if (fields.maxValue) query = query.replace(/Max_value/g, fields.maxValue);
  if (fields.minField) query = query.replace(/Min_Field/g, fields.minField);
  if (fields.maxField) query = query.replace(/Max_Field/g, fields.maxField);

  return query;
};
